import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "../components/Avatar";
import { PostComposer } from "../components/PostComposer";
import { PostCard } from "../components/PostCard";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Crown, Users, Check, Loader2, UserPlus, Search, Pencil, Camera, Flag, X, Lock, Globe, UserMinus, CalendarDays } from "lucide-react";
import { ReportDialog } from "../components/ReportDialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { mediaUrl } from "../lib/api";
import { toast } from "sonner";

function MemberRow({ u, canManage, onRemove, onOpen }) {
  return (
    <div className="flex items-center gap-2 border-2 border-black rounded-lg p-2 bg-spotter-cream" data-testid={`member-row-${u.id}`}>
      <button onClick={() => onOpen(u.id)}><Avatar user={u} size="sm" /></button>
      <button onClick={() => onOpen(u.id)} className="flex-1 text-left text-sm font-semibold hover:underline min-w-0 truncate">{u.name} <span className="text-xs text-spotter-maroon">@{u.username}</span></button>
      {canManage && (
        <Button size="sm" variant="outline" data-testid={`remove-member-${u.id}`} onClick={() => onRemove(u)} className="border-2 border-black text-spotter-maroon gap-1 shrink-0"><UserMinus className="w-4 h-4" />Remove</Button>
      )}
    </div>
  );
}

export default function SpotDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [spot, setSpot] = useState(null);
  const [posts, setPosts] = useState([]);
  const [inviteQ, setInviteQ] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [allFriends, setAllFriends] = useState([]);
  const [invitedIds, setInvitedIds] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", description: "", cover_url: null, visibility: "public" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [memberQ, setMemberQ] = useState("");
  const coverRef = useRef();

  const load = async () => {
    const { data } = await api.get(`/spots/${id}`);
    setSpot(data);
    const pr = await api.get(`/spots/${id}/posts`);
    setPosts(pr.data);
  };
  useEffect(() => { load(); }, [id]);

  const openInvite = async () => {
    setInviteOpen(true);
    try { const { data } = await api.get("/friends"); setAllFriends(data); }
    catch (e) { setAllFriends([]); }
  };
  const invite = async (uid) => {
    try { await api.post(`/spots/${id}/invite`, { to_user_id: uid }); setInvitedIds((prev) => [...prev, uid]); toast.success("Invite sent!"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (!spot) return <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-spotter-maroon" /></div>;

  const isPrivate = spot.visibility === "private";
  const join = async () => { try { const { data } = await api.post(`/spots/${id}/join`); toast.success(data.status === "pending" ? "Request sent to the leader" : "Joined!"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const leave = async () => { await api.post(`/spots/${id}/leave`); load(); };
  const approve = async (uid) => { await api.post(`/spots/${id}/approve`, { to_user_id: uid }); load(); toast.success("Member approved"); };
  const removeMember = async (u) => {
    if (!window.confirm(`Remove ${u.name} from this Hot Spot?`)) return;
    try { await api.post(`/spots/${id}/remove-member`, { to_user_id: u.id }); toast.success(`${u.name} removed`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const isMember = spot.my_status === "member" || spot.is_leader;
  const members = spot.members || [];

  const openEdit = () => { setEditForm({ name: spot.name, description: spot.description || "", cover_url: spot.cover_url || null, visibility: spot.visibility || "public" }); setEditOpen(true); };
  const pickCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingCover(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditForm((p) => ({ ...p, cover_url: data.url }));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploadingCover(false); e.target.value = ""; }
  };
  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error("Give your Spot a name."); return; }
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/spots/${id}`, editForm);
      setSpot((s) => ({ ...s, ...data }));
      setEditOpen(false); toast.success("Hot Spot updated!");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const created = spot.created_at ? new Date(spot.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "";

  return (
    <div className="max-w-5xl mx-auto">
      <Button variant="ghost" onClick={() => navigate("/spots")} className="mb-3" data-testid="back-to-spots"><ArrowLeft className="w-4 h-4 mr-2" />All Hot Spots</Button>

      {/* Cover header */}
      <div className="bg-white border-4 border-black rounded-2xl overflow-hidden brand-shadow mb-5">
        <div className="h-36 border-b-2 border-black bg-spotter-cream relative" style={spot.cover_url ? { backgroundImage: `url(${mediaUrl(spot.cover_url)})`, backgroundSize: "cover", backgroundPosition: "center" } : { backgroundImage: "var(--leopard)", backgroundSize: "cover" }}>
          <span data-testid="spot-visibility-badge" className="absolute top-3 left-3 flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border-2 border-black bg-spotter-cream text-spotter-maroon">
            {isPrivate ? <><Lock className="w-3.5 h-3.5" />Private</> : <><Globe className="w-3.5 h-3.5" />Public</>}
          </span>
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-2">
            <h2 className="font-display text-3xl font-bold text-spotter-maroon" data-testid="spot-title">{spot.name}</h2>
            <div className="flex items-center gap-2 shrink-0">
              {spot.can_edit && <Button data-testid="spot-edit-btn" size="sm" onClick={openEdit} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Pencil className="w-4 h-4" />Edit</Button>}
              {!spot.is_leader && <Button data-testid="spot-report-btn" size="sm" variant="outline" onClick={() => setReportOpen(true)} className="border-2 border-black gap-1"><Flag className="w-4 h-4" />Report</Button>}
            </div>
          </div>
          <p className="text-[15px] mt-1">{spot.description}</p>
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-spotter-maroon">
            <button onClick={() => navigate(`/profile/${spot.leader?.id}`)} className="flex items-center gap-1 hover:underline"><Crown className="w-4 h-4 text-spotter-gold" />{spot.leader?.name}</button>
            <button onClick={() => setMembersOpen(true)} data-testid="spot-members-count" className="flex items-center gap-1 hover:underline"><Users className="w-4 h-4" />{spot.member_count} members</button>
            {created && <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" />Since {created}</span>}
          </div>
          <div className="mt-3">
            {!spot.my_status && !spot.is_leader && <Button data-testid="spot-join-btn" onClick={join} className="bg-spotter-maroon text-spotter-cream border-2 border-black">{isPrivate ? "Request to Join" : "Join Spot"}</Button>}
            {spot.my_status === "pending" && <span className="text-sm text-spotter-maroon font-semibold">Your request is pending the leader's approval.</span>}
            {spot.my_status === "member" && <Button data-testid="spot-leave-btn" onClick={leave} variant="outline" className="border-2 border-black">Leave Spot</Button>}
          </div>
        </div>
      </div>

      {/* Two-column body */}
      <div className="grid lg:grid-cols-3 gap-5 items-start">
        {/* Main column */}
        <div className="lg:col-span-2 min-w-0">
          {isMember ? (
            <>
              <PostComposer spotId={id} onCreated={(p) => setPosts((prev) => [p, ...prev])} />
              {posts.length === 0
                ? <div className="text-center text-spotter-maroon py-6">No posts in this Spot yet.</div>
                : posts.map((p) => <PostCard key={p.id} post={p} onDeleted={(pid) => setPosts((prev) => prev.filter((x) => x.id !== pid))} />)}
            </>
          ) : (
            <div className="text-center text-spotter-maroon py-8 bg-white/80 rounded-xl border-2 border-black">
              {isPrivate ? "This is a private Hot Spot. Request to join to see the conversation." : "Join this Spot to post and see the conversation."}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-5 lg:sticky lg:top-4">
          {/* Members */}
          <div className="bg-white border-2 border-black rounded-xl p-4 brand-shadow-sm" data-testid="spot-members-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold flex items-center gap-2 text-spotter-maroon"><Users className="w-4 h-4" />Members <span className="text-sm font-normal">({spot.member_count})</span></h3>
              {members.length > 0 && <button data-testid="see-all-members" onClick={() => setMembersOpen(true)} className="text-sm text-spotter-maroon underline">See all</button>}
            </div>
            {members.length === 0 ? <p className="text-sm text-spotter-maroon">No members yet.</p> : (
              <div className="grid grid-cols-5 gap-2" data-testid="member-avatars">
                {members.slice(0, 10).map((u) => (
                  <button key={u.id} onClick={() => navigate(`/profile/${u.id}`)} title={u.name} className="flex flex-col items-center gap-1">
                    <Avatar user={u} size="sm" />
                    <span className="text-[10px] text-spotter-maroon truncate w-full text-center leading-tight">{u.name?.split(" ")[0]}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pending approvals */}
          {spot.can_manage && spot.pending?.length > 0 && (
            <div className="bg-white border-2 border-black rounded-xl p-4 brand-shadow-sm" data-testid="spot-pending">
              <h3 className="font-heading font-bold mb-2 text-spotter-maroon">Requests to join ({spot.pending.length})</h3>
              {spot.pending.map((u) => (
                <div key={u.id} className="flex items-center gap-2 py-1">
                  <Avatar user={u} size="sm" />
                  <span className="flex-1 text-sm font-semibold min-w-0 truncate">{u.name}</span>
                  <Button size="sm" data-testid={`approve-member-${u.id}`} onClick={() => approve(u.id)} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Check className="w-4 h-4" />Approve</Button>
                </div>
              ))}
            </div>
          )}

          {/* Invite */}
          {spot.can_manage && (
            <div className="bg-white border-2 border-black rounded-xl p-4 brand-shadow-sm" data-testid="spot-invite-panel">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold flex items-center gap-2 text-spotter-maroon"><UserPlus className="w-4 h-4" />Invite members</h3>
                {!inviteOpen
                  ? <Button size="sm" data-testid="spot-invite-open" onClick={openInvite} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><UserPlus className="w-4 h-4" />Invite</Button>
                  : <button data-testid="spot-invite-close" onClick={() => setInviteOpen(false)} className="text-spotter-maroon border-2 border-black rounded-md p-1 bg-white hover:bg-spotter-cream"><X className="w-4 h-4" /></button>}
              </div>
              {inviteOpen && (
                <>
                  <div className="relative mt-3">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-spotter-maroon" />
                    <Input data-testid="spot-invite-search" value={inviteQ} onChange={(e) => setInviteQ(e.target.value)} placeholder="Search your Pride…" className="pl-9 border-2 border-black" />
                  </div>
                  <div className="mt-2 space-y-1 max-h-72 overflow-y-auto" data-testid="spot-invite-list">
                    {allFriends
                      .filter((u) => !inviteQ.trim() || u.name.toLowerCase().includes(inviteQ.toLowerCase()) || (u.username || "").toLowerCase().includes(inviteQ.toLowerCase()))
                      .map((u) => {
                        const invited = invitedIds.includes(u.id);
                        return (
                          <div key={u.id} className="flex items-center gap-2 border-2 border-black rounded-lg p-2 bg-spotter-cream">
                            <Avatar user={u} size="sm" />
                            <span className="flex-1 text-sm font-semibold min-w-0 truncate">{u.name} <span className="text-xs text-spotter-maroon">@{u.username}</span></span>
                            <Button size="sm" data-testid={`invite-${u.id}`} disabled={invited} onClick={() => invite(u.id)}
                              className="border-2 border-black gap-1 text-spotter-cream shrink-0"
                              style={{ backgroundColor: invited ? "#E19C00" : undefined }}>
                              {invited ? <><Check className="w-4 h-4" />Invited</> : <><UserPlus className="w-4 h-4" />Invite</>}
                            </Button>
                          </div>
                        );
                      })}
                    {allFriends.length === 0 && <p className="text-sm text-spotter-maroon py-2 text-center">Your Pride is empty — add friends to invite them here.</p>}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Members popup */}
      <Dialog open={membersOpen} onOpenChange={setMembersOpen}>
        <DialogContent className="border-4 border-black bg-spotter-cream max-h-[90dvh] overflow-y-auto overscroll-contain top-[5dvh] translate-y-0" data-testid="members-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-spotter-maroon flex items-center gap-2"><Users className="w-5 h-5" />Members ({spot.member_count})</DialogTitle></DialogHeader>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-spotter-maroon" />
            <Input data-testid="members-search" value={memberQ} onChange={(e) => setMemberQ(e.target.value)} placeholder="Search members…" className="pl-9 border-2 border-black" />
          </div>
          <div className="space-y-2 mt-2">
            {members
              .filter((u) => !memberQ.trim() || u.name.toLowerCase().includes(memberQ.toLowerCase()) || (u.username || "").toLowerCase().includes(memberQ.toLowerCase()))
              .map((u) => (
                <MemberRow key={u.id} u={u} canManage={spot.can_manage && u.id !== spot.leader?.id}
                  onRemove={removeMember} onOpen={(uid) => { setMembersOpen(false); navigate(`/profile/${uid}`); }} />
              ))}
            {members.length === 0 && <p className="text-sm text-spotter-maroon text-center py-4">No members yet.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="border-4 border-black bg-spotter-cream max-h-[90dvh] overflow-y-auto overscroll-contain top-[5dvh] translate-y-0" data-testid="spot-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-spotter-maroon">Edit Hot Spot</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="relative h-28 rounded-lg border-2 border-black overflow-hidden bg-spotter-cream" style={editForm.cover_url ? { backgroundImage: `url(${mediaUrl(editForm.cover_url)})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
              <input ref={coverRef} type="file" accept="image/*" hidden onChange={pickCover} />
              <Button data-testid="spot-cover-btn" size="sm" onClick={() => coverRef.current?.click()} disabled={uploadingCover} className="absolute bottom-2 right-2 bg-black/70 text-white border border-white gap-2">{uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}Cover</Button>
            </div>
            <div><Label>Spot name</Label><Input data-testid="spot-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="border-2 border-black" /></div>
            <div><Label>Description</Label><Textarea data-testid="spot-edit-desc" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="border-2 border-black" /></div>
            <div>
              <Label>Visibility</Label>
              <div className="flex gap-2 mt-1" data-testid="spot-edit-visibility">
                <button type="button" onClick={() => setEditForm({ ...editForm, visibility: "public" })} data-testid="spot-edit-public"
                  className={`flex-1 flex items-center gap-2 border-2 border-black rounded-lg p-2 text-left text-sm ${editForm.visibility === "public" ? "bg-spotter-maroon text-spotter-cream" : "bg-white text-spotter-maroon"}`}>
                  <Globe className="w-4 h-4" /><span><b>Public</b> — join instantly</span>
                </button>
                <button type="button" onClick={() => setEditForm({ ...editForm, visibility: "private" })} data-testid="spot-edit-private"
                  className={`flex-1 flex items-center gap-2 border-2 border-black rounded-lg p-2 text-left text-sm ${editForm.visibility === "private" ? "bg-spotter-maroon text-spotter-cream" : "bg-white text-spotter-maroon"}`}>
                  <Lock className="w-4 h-4" /><span><b>Private</b> — approve requests</span>
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="spot-edit-save" onClick={saveEdit} disabled={savingEdit} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-2">{savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} targetType="group" targetId={id} targetName={spot.name} />
    </div>
  );
}
